/**
 * `<SettingsCard>` — the v4 `.s-card` primitive (P2, GAP_ANALYSIS.md §5 / §7.2 +
 * DESIGN_HANDOFF.md §6 Settings).
 *
 * Replaces the shadcn `<Card>` wrapper (`SettingsSection`) for new Settings
 * sub-components. The chrome is hand-rolled CSS (.s-card / .s-card-head /
 * .s-card-title / .s-card-state / .s-card-body), matching the v4 prototype's
 * settings spec verbatim: --bg-1 fill, --line-soft border, --rad-lg radius,
 * 20×22 padding, sans 15px/600 title, optional health pill on the right.
 *
 * Per DESIGN_HANDOFF.md §9: "Tailwind: keep it for PageHead.tsx and the
 * dashboard cards only. Do not retrofit the surfaces." The shadcn Card path
 * was a regression from that rule; SettingsCard is the canonical
 * replacement for Settings sub-components.
 *
 * Usage:
 *
 *   <SettingsCard
 *     id="api-keys"
 *     title="API keys"
 *     desc="Stored in the OS keychain, never displayed again."
 *     state={{ kind: 'good', label: 'All keys stored' }}
 *     fullWidth
 *   >
 *     ...body...
 *   </SettingsCard>
 */

import type { ReactNode } from 'react';

/** Health pill rendered top-right of the card head. */
export interface SettingsCardState {
  kind: 'good' | 'warn' | 'bad' | 'idle';
  /** Short uppercase-mono label — e.g. "All keys stored", "Connected · 12ms", "Within budget". */
  label: string;
}

interface SettingsCardProps {
  /** Anchored deep-link id (e.g. `?section=api-keys`). */
  id?: string;
  /** h3 title; renders in sans 15px/600 per the v4 .s-card-title spec. */
  title: ReactNode;
  /** Optional one-line description below the title in --fg-3. */
  desc?: ReactNode;
  /** Optional state pill rendered top-right of the head — health/connection summary. */
  state?: SettingsCardState;
  /** Spans both columns of the .settings-grid 2-col layout. */
  fullWidth?: boolean;
  children: ReactNode;
}

export function SettingsCard({
  id,
  title,
  desc,
  state,
  fullWidth = false,
  children,
}: SettingsCardProps): JSX.Element {
  const className = fullWidth ? 's-card full' : 's-card';
  return (
    <section className={className} id={id}>
      <div className="s-card-head">
        <h3 className="s-card-title">
          {title}
          {desc !== undefined && <span className="desc">{desc}</span>}
        </h3>
        {state !== undefined && (
          <span className={stateClass(state.kind)}>
            <span className="dot" aria-hidden="true" />
            {state.label}
          </span>
        )}
      </div>
      <div className="s-card-body">{children}</div>
    </section>
  );
}

function stateClass(kind: SettingsCardState['kind']): string {
  // The default .s-card-state is green ("good"); the modifier classes flip
  // color + dot to warn / bad / idle. Matches the v4 settings.css spec.
  if (kind === 'good') return 's-card-state';
  return `s-card-state s-card-state--${kind}`;
}
